import React, { useState, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  FaPlus, FaSearch, FaFilter, FaSort, 
  FaMoneyBillWave, FaCreditCard, FaMobileAlt,
  FaUniversity, FaEdit, FaTrash, FaSpinner,
  FaExclamationTriangle, FaInfoCircle
} from 'react-icons/fa';
import './PaymentMethods.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';


// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const PaymentMethods = () => {
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);

  const paymentTypes = {
    bank_transfer: {
      label: 'Bank Transfer',
      icon: <FaUniversity />,
      color: '#1a237e'
    },
    mobile_payment: {
      label: 'Mobile Payment',
      icon: <FaMobileAlt />,
      color: '#2e7d32'
    },
    cash: {
      label: 'Cash',
      icon: <FaMoneyBillWave />,
      color: '#c62828'
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_BASE_URL}/payment-methods/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch payment methods');
      }

      // Convert balance to number for each payment method
      const processedData = data.map(method => ({
        ...method,
        balance: parseFloat(method.balance) || 0
      }));

      setPaymentMethods(processedData);
      setError('');
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      if (error.message === 'Session expired') {
        setTokenExpired(true);
        showSessionExpiredAlert();
      } else {
        setError(error.message);
        showErrorAlert('Failed to load payment methods');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Payment Method',
        text: 'Are you sure you want to delete this payment method? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#64748b',
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        const token = localStorage.getItem('authToken');
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(`${API_BASE_URL}/payment-methods/${id}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (response.status === 401) {
          throw new Error('Session expired');
        }

        if (!response.ok) {
          throw new Error('Failed to delete payment method');
        }

        setPaymentMethods(prev => prev.filter(method => method.id !== id));
        showSuccessAlert('Payment method deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      if (error.message === 'Session expired') {
        setTokenExpired(true);
        showSessionExpiredAlert();
      } else {
        showErrorAlert('Failed to delete payment method');
      }
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message
    });
  };

  const showSessionExpiredAlert = () => {
    Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired. Please log in again.',
      icon: 'warning',
      confirmButtonColor: '#1a237e',
      confirmButtonText: 'Go to Login',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/login');
      }
    });
  };

  const filteredAndSortedMethods = paymentMethods
    .filter(method => {
      const matchesSearch = method.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          method.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterType === 'all' || method.payment_type === filterType;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortConfig.key === 'balance') {
        return sortConfig.direction === 'asc'
          ? a.balance - b.balance
          : b.balance - a.balance;
      }
      return 0;
    });

  if (tokenExpired) {
    return (
      <Layout>
        <div className="error-banner token-expired">
          <FaExclamationTriangle size={32} />
          <span>Your session has expired. Please log in to continue.</span>
          <button 
            className="btn-primary"
            onClick={() => navigate('/login')}
          >
            Go to Login
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="payment-methods">
        <div className="page-header">
          <div className="header-content">
            <h1>Payment Methods</h1>
            <p>Manage your payment methods and track their balances</p>
          </div>
          <button 
            className="btn-primary"
            onClick={() => navigate('/payment-methods/create')}
          >
            <FaPlus /> Add Payment Method
          </button>
        </div>

        <div className="toolbar">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search payment methods..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="toolbar-actions">
            <button 
              className={`btn-secondary ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filter
            </button>
            <div className="sort-dropdown">
              <button className="btn-secondary">
                <FaSort /> Sort
              </button>
              <div className="sort-options">
                <button onClick={() => handleSort('name')}>
                  Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </button>
                <button onClick={() => handleSort('balance')}>
                  Balance {sortConfig.key === 'balance' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="filter-options">
            <button 
              className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
              onClick={() => setFilterType('all')}
            >
              All
            </button>
            {Object.entries(paymentTypes).map(([type, { label, icon }]) => (
              <button
                key={type}
                className={`filter-btn ${filterType === type ? 'active' : ''}`}
                onClick={() => setFilterType(type)}
              >
                {icon} {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <FaSpinner className="spinner" />
            <span>Loading payment methods...</span>
          </div>
        ) : error ? (
          <div className="error-state">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        ) : filteredAndSortedMethods.length === 0 ? (
          <div className="empty-state">
            <FaInfoCircle />
            <span>No payment methods found</span>
            <button 
              className="btn-secondary"
              onClick={() => navigate('/payment-methods/create')}
            >
              Create your first payment method
            </button>
          </div>
        ) : (
          <div className="payment-methods-grid">
            {filteredAndSortedMethods.map(method => {
              const typeInfo = paymentTypes[method.payment_type];
              return (
                <div key={method.id} className="payment-method-card">
                  <div className="card-header">
                    <div 
                      className="type-icon"
                      style={{ backgroundColor: typeInfo.color }}
                    >
                      {typeInfo.icon}
                    </div>
                    <div className="card-actions">
                      <button 
                        className="btn-icon"
                        onClick={() => navigate(`/payment-methods/edit/${method.id}`)}
                        title="Edit payment method"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="btn-icon delete"
                        onClick={() => handleDelete(method.id)}
                        title="Delete payment method"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="card-content">
                    <h3>{method.name}</h3>
                    <p className="type-label">{typeInfo.label}</p>
                    <div className="balance">
                      <span className="label">Balance</span>
                      <span className="amount">
                        ${typeof method.balance === 'number' ? method.balance.toFixed(2) : '0.00'}
                      </span>
                    </div>
                    {method.notes && (
                      <p className="notes">{method.notes}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentMethods;
